import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePermissions } from '@mint-lib/profile-helpers';

import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Typography,
} from '@myn/mui';

import ConditionalCurrencyField from '../../../atoms/ConditionalCurrencyField.jsx';
import { useCampaignDataSelector } from '../../../hooks/useGetCampaignData.js';
import type { Currency } from '../../../types/_campaigns/campaigns.js';
import {
  BudgetType,
  BudgetValuesType,
  Version,
} from '../../../types/versions.js';
import {
  ADSERVER_COST,
  DSP_COST,
  NOT_INTEGRATED_COLUMNS,
} from '../../EditModal/EditModal.constants.js';
import { getBudgetLabels } from '../constants/index.jsx';
import BudgetDifference from './BudgetDifference.jsx';

const ZERO_BUDGET = '0.00';

type BudgetTableProps = {
  item: Version;
  versions?: Version[];
};
type BudgetValues = Omit<BudgetType, 'total'>;

type BudgetItemType = {
  label: string;
  value: BudgetValuesType;
};

type NoTechCostType = {
  [ADSERVER_COST.COL]: boolean;
  [DSP_COST.COL]: boolean;
};

const BudgetTable: React.FC<BudgetTableProps> = ({ item, versions }) => {
  const { t } = useTranslation('@myn/planning');

  const { permissions } = usePermissions();
  const [techCostAccess, setTechCostAccess] = useState<NoTechCostType | null>(
    null,
  );
  const adServerCostAccess = !!permissions.adserverCostFeature;
  const dspCostAccess = !!permissions.dpsCostFeature;

  const { getPermissionField } = usePermissions();
  const isNonFin = !getPermissionField('financialDataFeature');

  const { data: currency } = useCampaignDataSelector('currency');

  const [budgetValues, setBudgetValues] = useState<BudgetValues>();

  const budgetsArray = useMemo(() => {
    const budgetLabels = getBudgetLabels(t);

    return Object.entries(budgetValues || [])
      .map((b) => ({
        label: budgetLabels[b[0] as keyof typeof budgetLabels],
        value: b[1],
      }))
      .filter((b) =>
        techCostAccess &&
        (!adServerCostAccess || !dspCostAccess) &&
        NOT_INTEGRATED_COLUMNS.includes(b.label)
          ? techCostAccess[b.label as keyof typeof techCostAccess]
          : b,
      );
  }, [budgetValues, techCostAccess, t]);

  const totalBudget = useMemo(() => {
    return budgetsArray.reduce((acc: number, next: BudgetItemType) => {
      acc = acc + Number(next.value.current);
      return acc;
    }, 0);
  }, [budgetsArray]);

  const renderDifferenceChip = ({
    currentBudget,
    previousBudget,
    difference,
    currency,
  }: {
    currentBudget: string | null;
    previousBudget: string | null;
    difference: string | null;
    currency?: Currency | null;
  }) => {
    const isHidden =
      !currentBudget || !previousBudget || !difference || !currency;

    if (isHidden) {
      return null;
    }

    const differenceNumber = Number(difference.replace('%', ''));
    const currentBudgetNumber = Number(currentBudget);
    const previousBudgetNumber = Number(previousBudget);

    if (!differenceNumber) {
      return null;
    }

    return (
      <BudgetDifference
        currentBudget={currentBudgetNumber}
        previousBudget={previousBudgetNumber}
        differencePercent={differenceNumber}
        currency={currency}
      />
    );
  };

  useEffect(() => {
    if (item.channelMixVersionData?.budget) {
      const { total, ...restBudgetValues } = item.channelMixVersionData.budget;
      setBudgetValues(restBudgetValues);
    }
  }, [item.channelMixVersionData]);

  useEffect(() => {
    setTechCostAccess({
      [ADSERVER_COST.COL]: adServerCostAccess,
      [DSP_COST.COL]: dspCostAccess,
    });
  }, [adServerCostAccess, dspCostAccess]);

  if (isNonFin) {
    return null;
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('Buying Plan column')}</TableHeaderCell>
            <TableHeaderCell sx={{ justifyItems: 'flex-end' }}>
              {t('Budget')}
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableBodyCell>
              <Typography variant="h6" color="text.primary">
                {t('Totals')}
              </Typography>
            </TableBodyCell>
            <TableBodyCell align="right">
              <Typography variant="h6" color="text.primary">
                <ConditionalCurrencyField
                  currency={currency}
                  value={totalBudget}
                />
              </Typography>
            </TableBodyCell>
          </TableRow>
          {budgetsArray.map((budget) => (
            <TableRow key={budget.label}>
              <TableBodyCell>{budget.label}</TableBodyCell>
              <TableBodyCell align="right">
                {renderDifferenceChip({
                  currentBudget: budget.value.current ?? null,
                  previousBudget: budget.value.previous ?? null,
                  difference: budget.value.difference ?? null,
                  currency: currency,
                })}
                <ConditionalCurrencyField
                  currency={currency}
                  value={budget.value.current ?? ZERO_BUDGET}
                />
              </TableBodyCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BudgetTable;
